import type {
  TaskSignatureResponse,
  TaskStatus,
} from '../../services/api/types.gen';
import { COMPLETED_STATUSES } from '../../constants/statuses';

const DEFAULT_ROLE_LABELS: Record<string, string> = {
  clinician: 'clinician',
  case_manager: 'case manager',
};

const getRole = (sig: TaskSignatureResponse): string | undefined => {
  const meta = sig.signature_metadata as Record<string, unknown> | null;
  return meta?.signer_role as string | undefined;
};

const findByRole = (
  sigs: TaskSignatureResponse[],
  role: string
): TaskSignatureResponse | undefined => sigs.find(s => getRole(s) === role);

const signerName = (sig: TaskSignatureResponse): string => {
  const meta = (sig.signature_metadata as Record<string, unknown> | null) ?? {};
  return (
    (meta.signer_display_name as string) ||
    (meta.user_name as string) ||
    (meta.user_email as string) ||
    'Unknown signer'
  );
};

interface SignedSigner {
  id: string;
  name: string;
  /** Display label for the signer's role (e.g. "case manager"). */
  roleLabel?: string;
  signedAt: string;
}

export interface SignatureSummaryData {
  /** Task is in a terminal status — treated as fully signed. */
  complete: boolean;
  signed: SignedSigner[];
  /** Human-readable awaiting labels, e.g. 'case manager', '1 signature'. */
  awaiting: string[];
}

/**
 * Reduces a task's active signatures to a compact signed/awaiting summary.
 *
 * The branching mirrors the legacy SignatureStatusBadges pills (ROG-2553):
 * role-aware when `signer_role` metadata is present, count-based otherwise, and
 * a terminal/completed status is always treated as fully signed so single-signer
 * tasks don't falsely demand a second signature next to a "Completed" status.
 */
export function computeSignatureSummary(
  activeSignatures: TaskSignatureResponse[],
  status?: TaskStatus
): SignatureSummaryData {
  const labelForRole = (role: string): string =>
    DEFAULT_ROLE_LABELS[role] ?? role.replace(/_/g, ' ');

  const toSigned = (sig: TaskSignatureResponse): SignedSigner => {
    const role = getRole(sig);
    return {
      id: sig.id,
      name: signerName(sig),
      roleLabel: role ? labelForRole(role) : undefined,
      signedAt: sig.signed_at,
    };
  };

  const clinicianSig = findByRole(activeSignatures, 'clinician');
  const caseManagerSig = findByRole(activeSignatures, 'case_manager');
  const unknownRoleSigs = activeSignatures.filter(s => !getRole(s));
  const total = Math.min(activeSignatures.length, 2);
  const isComplete = status ? COMPLETED_STATUSES.has(status) : false;

  if (isComplete) {
    return {
      complete: true,
      signed: activeSignatures.slice(0, 2).map(toSigned),
      awaiting: [],
    };
  }
  if (total === 0) {
    return { complete: false, signed: [], awaiting: ['2 signatures'] };
  }
  if (total >= 2) {
    return {
      complete: false,
      signed: activeSignatures.slice(0, 2).map(toSigned),
      awaiting: [],
    };
  }
  if (clinicianSig) {
    return {
      complete: false,
      signed: [toSigned(clinicianSig)],
      awaiting: [labelForRole('case_manager')],
    };
  }
  if (caseManagerSig) {
    return {
      complete: false,
      signed: [toSigned(caseManagerSig)],
      awaiting: [labelForRole('clinician')],
    };
  }
  const sig = unknownRoleSigs[0] ?? activeSignatures[0];
  return {
    complete: false,
    signed: [toSigned(sig)],
    awaiting: ['1 signature'],
  };
}
